import _ from "lodash";
import { PARTICIPANT_USER_TYPES } from "MyCalendar/modules/MyCalendarModule/MyCalendarConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

export const USER_TYPE_COUNT_FIELDS = {
  STAFF: "staffCount",
  STUDENT: "studentCount",
  FAMILY_MEMBER: "familyMemberCount",
  PARENT: "familyMemberCount",
};

/**
 * Builds a comma-separated subtext string summarising participant counts per user type.
 * Mirrors the getSubtextString helper in ParticipantsPreview.
 * @param {{ t: Function, item: object, sharedWith: string[] }} params
 * @returns {string}
 */
export const getSubtextString = ({ t, item, sharedWith }) =>
  _.map(sharedWith, userType => {
    const count = _.get(item, USER_TYPE_COUNT_FIELDS[userType]);
    return `${count} ${t(PARTICIPANT_USER_TYPES[userType], { count })}`;
  }).join(", ");

export const UserTypeMapping = {
  Student: "student",
  Staff: "staff",
  FamilyMember: "parent",
};

/**
 * Builds the display name for a FamilyMember participant.
 * Format: "Family of <first child name>" or "Family of <first child name> +N more".
 * @param {{ participant: object, t: Function, defaultStudentNameOrderPreference: string }} params
 * @returns {string}
 */
export const getParentDisplayName = ({
  participant,
  t,
  defaultStudentNameOrderPreference,
}) => {
  const children = _.get(participant, "children", []);
  if (_.isEmpty(children)) {
    return "";
  }
  const firstChildName = getFormattedUserName({
    user: _.first(children),
    type: "student",
    defaultStudentNameOrderPreference,
  });
  const remainingCount = children.length - 1;
  if (remainingCount > 0) {
    return t("common:family_of_label_and_count", {
      label: firstChildName,
      count: remainingCount,
    });
  }
  return t("common:family_of_label", { label: firstChildName });
};

const getSubtext = ({
  participant,
  userType,
  t,
  defaultStudentNameOrderPreference,
}) => {
  const isStudent = userType === UserTypeMapping.Student;

  return isStudent
    ? _.get(participant, "yearGroup.grade.name", "")
    : getParentDisplayName({
        participant,
        t,
        defaultStudentNameOrderPreference,
      });
};

/**
 * Normalises a participant (Student/Staff/FamilyMember) into the list item shape
 * expected by ParticipantList.
 * @param {object} participant
 * @param {string} defaultStudentNameOrderPreference
 * @param {Function} t
 * @returns {{ id: string, name: string, subText: string, profileImage: string, alertTypes: string[] }}
 */
export const normaliseParticipant = (
  participant,
  defaultStudentNameOrderPreference,
  t
) => {
  const userType = participant?.type || UserTypeMapping[participant.__typename];
  return {
    id: participant.id,
    name: getFormattedUserName({
      user: participant,
      type: userType,
      defaultStudentNameOrderPreference,
    }),
    profileImage: _.get(participant, "profileImage", ""),
    subText: getSubtext({
      participant,
      userType,
      t,
      defaultStudentNameOrderPreference,
    }),
    alertTypes: participant?.alertTypes || [],
  };
};
